import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Layers, RefreshCw, Loader2, AlertTriangle, Building2, Wallet,
  Sparkles, TrendingUp, TrendingDown, ArrowDownUp, Eye, EyeOff, Info,
} from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { getAggregatorSummary, getAggregatorByAsset, getAggregatorPnl } from '../services/api';
import { InfoTip } from '../components/ui';

// ─── helpers ────────────────────────────────────────────────────────────────

function formatUsd(n, { hidden } = {}) {
  if (hidden) return '••••••';
  if (n == null || !Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(2)}K`;
  return `$${n.toFixed(2)}`;
}

function formatNum(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 1) return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
  return n.toLocaleString(undefined, { maximumFractionDigits: 8 });
}

function sourceBadge(source) {
  const map = {
    live:        { label: 'LIVE',        cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
    cached:      { label: 'CACHED',      cls: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30' },
    stale:       { label: 'STALE',       cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
    unavailable: { label: 'UNAVAILABLE', cls: 'bg-red-500/15 text-red-300 border-red-500/30' },
  };
  const s = map[source] || { label: source?.toUpperCase() || '—', cls: 'bg-dark-700 text-gray-400 border-dark-600' };
  return <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded border ${s.cls}`}>{s.label}</span>;
}

function venueIcon(venueType) {
  if (venueType === 'cex') return <Building2 className="w-4 h-4 text-blue-400" />;
  return <Wallet className="w-4 h-4 text-violet-400" />;
}

// ─── page ──────────────────────────────────────────────────────────────────

function todayISO() { return new Date().toISOString().slice(0, 10); }
function daysAgoISO(d) { return new Date(Date.now() - d * 86400000).toISOString().slice(0, 10); }

export default function AggregatorPage() {
  const [snapshot, setSnapshot] = useState(null);
  const [assets, setAssets] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [view, setView] = useState('venues'); // 'venues' | 'assets'
  const [hideBalances, setHideBalances] = useState(false);
  // pnlPreset: '7' | '30' | '60' | '90' | 'custom'
  const [pnlPreset, setPnlPreset] = useState('30');
  const [pnlStart, setPnlStart] = useState(daysAgoISO(30));
  const [pnlEnd, setPnlEnd] = useState(todayISO());

  const load = useCallback(async (forceRefresh = false) => {
    try {
      if (forceRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);
      const pnlArgs = pnlPreset === 'custom'
        ? { startDate: pnlStart, endDate: pnlEnd }
        : { days: Number(pnlPreset) };
      const [snap, byAsset, pnlData] = await Promise.all([
        getAggregatorSummary({ refresh: forceRefresh }),
        getAggregatorByAsset(),
        getAggregatorPnl(pnlArgs),
      ]);
      setSnapshot(snap?.data || snap);
      setAssets((byAsset?.data || byAsset)?.assets || []);
      setPnl(pnlData?.data || pnlData);
    } catch (e) {
      setError(e?.message || 'Failed to load aggregator data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [pnlPreset, pnlStart, pnlEnd]);

  useEffect(() => { load(false); }, [load]);

  // Convenience label for the KPI card sub-text
  const pnlLabel = pnlPreset === 'custom'
    ? `${pnlStart} → ${pnlEnd}`
    : `${pnlPreset}-day return`;

  const venues = snapshot?.venues || [];
  const totals = snapshot?.totals || { totalUsd: 0, totalCex: 0, totalWallets: 0, venueCount: 0, cexCount: 0, walletCount: 0 };

  const pnlSeries = pnl?.series || [];
  const cumReturnPct = pnl?.cumulativeReturnPct;
  const pnlPositive = (cumReturnPct ?? 0) >= 0;

  const unavailableVenues = useMemo(() => venues.filter(v => v.source === 'unavailable'), [venues]);

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-cyan-500/10 via-dark-800/80 to-dark-900 rounded-2xl border border-cyan-500/20 p-6">
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-cyan-400/10 blur-3xl pointer-events-none" />
        <div className="relative flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-cyan-500/20 border border-cyan-400/30 flex items-center justify-center flex-shrink-0">
              <Layers className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold text-white">Multi-Account &amp; Multi-Wallet Aggregator</h1>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-400 border border-cyan-500/30 font-mono">Elite · Trader's Suite</span>
              </div>
              <p className="text-xs text-cyan-200/70 mt-1 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> Every connected exchange + on-chain wallet in one unified portfolio view. Live balances, no fabrication.
                <InfoTip side="bottom">Source badges: LIVE = just fetched, CACHED = ≤60 min, STALE = older, UNAVAILABLE = fetch failed (shows "—" not "$0"). Wallets are read-only — address only, no signing.</InfoTip>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setHideBalances(v => !v)}
              className="text-xs px-3 py-2 rounded-lg bg-dark-700/70 border border-dark-600 text-gray-300 hover:bg-dark-600 transition flex items-center gap-1.5"
              title={hideBalances ? 'Show balances' : 'Hide balances'}
            >
              {hideBalances ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              {hideBalances ? 'Show' : 'Hide'}
            </button>
            <button
              onClick={() => load(true)}
              disabled={refreshing}
              className="text-xs px-3 py-2 rounded-lg bg-cyan-500/20 border border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/30 transition flex items-center gap-1.5 disabled:opacity-50"
            >
              {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Refresh
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-sm text-red-300 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={<Layers className="w-4 h-4 text-cyan-300" />}
          label="Total portfolio value"
          value={formatUsd(totals.totalUsd, { hidden: hideBalances })}
          sub={`${totals.venueCount} venue${totals.venueCount === 1 ? '' : 's'}`}
        />
        <KpiCard
          icon={<Building2 className="w-4 h-4 text-blue-300" />}
          label="CEX balances"
          value={formatUsd(totals.totalCex, { hidden: hideBalances })}
          sub={`${totals.cexCount} exchange${totals.cexCount === 1 ? '' : 's'}`}
        />
        <KpiCard
          icon={<Wallet className="w-4 h-4 text-violet-300" />}
          label="Wallet balances"
          value={formatUsd(totals.totalWallets, { hidden: hideBalances })}
          sub={`${totals.walletCount} wallet${totals.walletCount === 1 ? '' : 's'}`}
        />
        <KpiCard
          icon={pnlPositive ? <TrendingUp className="w-4 h-4 text-emerald-300" /> : <TrendingDown className="w-4 h-4 text-red-300" />}
          label={pnlLabel}
          value={pnl?.series ? `${pnlPositive ? '+' : ''}${(cumReturnPct ?? 0).toFixed(2)}%` : '—'}
          sub={pnl?.message || '—'}
          tone={pnl?.series ? (pnlPositive ? 'positive' : 'negative') : 'neutral'}
        />
      </div>

      {loading && !snapshot && (
        <div className="text-center py-12 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-cyan-400" />
          Loading portfolio across all venues...
        </div>
      )}

      {!loading && snapshot && venues.length === 0 && (
        <div className="text-center py-12 bg-dark-800/50 border border-dark-700 rounded-2xl">
          <Layers className="w-10 h-10 text-gray-500 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-white mb-1">No connected venues yet</h3>
          <p className="text-sm text-gray-400 max-w-md mx-auto">
            Connect an exchange in <span className="text-cyan-300">Settings → Exchanges</span> or save an on-chain wallet to see your unified portfolio here.
          </p>
        </div>
      )}

      {snapshot && venues.length > 0 && (
        <>
          {/* P&L sparkline */}
          {pnlSeries.length > 0 && (
            <div className="bg-dark-800/60 border border-dark-700 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <ArrowDownUp className="w-4 h-4 text-cyan-400" />
                  Aggregate P&amp;L · {pnlPreset === 'custom' ? `${pnlStart} → ${pnlEnd}` : `last ${pnlPreset} days`}
                </h3>
                <div className="flex flex-wrap gap-1 items-center">
                  {['7', '30', '60', '90', 'custom'].map(p => (
                    <button
                      key={p}
                      onClick={() => setPnlPreset(p)}
                      className={`text-[11px] px-2 py-1 rounded border font-mono transition ${pnlPreset === p ? 'bg-cyan-500/20 border-cyan-500/40 text-cyan-200' : 'bg-dark-700 border-dark-600 text-gray-400 hover:text-gray-200'}`}
                    >{p === 'custom' ? 'Custom' : `${p}d`}</button>
                  ))}
                </div>
              </div>

              {pnlPreset === 'custom' && (
                <div className="flex flex-wrap items-end gap-2 mb-3 pb-3 border-b border-dark-700/60">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-gray-500 font-mono block mb-0.5">From</label>
                    <input
                      type="date"
                      value={pnlStart}
                      max={pnlEnd}
                      onChange={e => setPnlStart(e.target.value)}
                      className="text-xs px-2 py-1 rounded bg-dark-900/60 border border-dark-700 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-gray-500 font-mono block mb-0.5">To</label>
                    <input
                      type="date"
                      value={pnlEnd}
                      min={pnlStart}
                      max={todayISO()}
                      onChange={e => setPnlEnd(e.target.value)}
                      className="text-xs px-2 py-1 rounded bg-dark-900/60 border border-dark-700 text-white"
                    />
                  </div>
                  <div className="text-[10px] text-gray-500 ml-auto flex items-center gap-1">
                    <Info className="w-3 h-3" /> Max 365-day lookback
                  </div>
                </div>
              )}
              <div className="h-32">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={pnlSeries}>
                    <XAxis dataKey="date" hide />
                    <YAxis hide domain={['auto', 'auto']} />
                    <Tooltip
                      contentStyle={{ background: '#0a0f1e', border: '1px solid #1f2937', borderRadius: 8, fontSize: 11 }}
                      labelStyle={{ color: '#9ca3af' }}
                      formatter={(v) => [`${Number(v).toFixed(2)}%`, 'Cumulative']}
                    />
                    <Line type="monotone" dataKey="cumulativeReturnPct" stroke={pnlPositive ? '#10b981' : '#ef4444'} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="text-[11px] text-gray-500 mt-2 flex items-start gap-1">
                <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
                <span>
                  Date range affects this P&amp;L only — current balances above are always real-time.
                  Native bot accounting (Grid Bot / DCA) + cost-basis FIFO across CEX / DEX / algo.
                  Same numbers as Risk Overview.
                </span>
              </p>
            </div>
          )}

          {/* View toggle */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setView('venues')}
              className={`text-xs px-3 py-1.5 rounded-lg border transition ${view === 'venues' ? 'bg-cyan-500/20 border-cyan-500/40 text-cyan-200' : 'bg-dark-700 border-dark-600 text-gray-400 hover:text-gray-200'}`}
            >Grouped by venue</button>
            <button
              onClick={() => setView('assets')}
              className={`text-xs px-3 py-1.5 rounded-lg border transition ${view === 'assets' ? 'bg-cyan-500/20 border-cyan-500/40 text-cyan-200' : 'bg-dark-700 border-dark-600 text-gray-400 hover:text-gray-200'}`}
            >Grouped by asset</button>
            <div className="ml-auto text-[11px] text-gray-500">
              As of {snapshot.asOf ? new Date(snapshot.asOf).toLocaleTimeString() : '—'}
              {snapshot.fromCache && <span className="ml-1 text-amber-400">· cached</span>}
            </div>
          </div>

          {unavailableVenues.length > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 text-xs text-amber-200 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">{unavailableVenues.length} venue{unavailableVenues.length === 1 ? '' : 's'} could not be reached this refresh</div>
                <div className="text-amber-200/80 mt-0.5">
                  {unavailableVenues.map(v => v.label).join(', ')}. We never fabricate balances — totals shown reflect only venues that responded.
                </div>
              </div>
            </div>
          )}

          {/* Body */}
          {view === 'venues' ? (
            <div className="space-y-4">
              {venues.map(v => (
                <VenueCard key={v.venueId} venue={v} hideBalances={hideBalances} />
              ))}
            </div>
          ) : (
            <AssetGroupedTable assets={assets} hideBalances={hideBalances} />
          )}
        </>
      )}
    </div>
  );
}

function KpiCard({ icon, label, value, sub, tone = 'neutral' }) {
  const valueColor = tone === 'positive' ? 'text-emerald-300' : tone === 'negative' ? 'text-red-300' : 'text-white';
  return (
    <div className="bg-dark-800/60 border border-dark-700 rounded-xl p-4">
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-mono flex items-center gap-1.5 mb-2">
        {icon}<span>{label}</span>
      </div>
      <div className={`text-2xl font-bold ${valueColor}`}>{value}</div>
      <div className="text-[11px] text-gray-500 mt-1 truncate">{sub}</div>
    </div>
  );
}

function VenueCard({ venue, hideBalances }) {
  const [expanded, setExpanded] = useState(true);
  const isUnavailable = venue.source === 'unavailable';
  const accent = venue.venueType === 'cex' ? 'blue' : 'violet';
  const accentRing = accent === 'blue' ? 'border-blue-500/20' : 'border-violet-500/20';

  return (
    <div className={`bg-dark-800/60 border ${accentRing} rounded-2xl overflow-hidden`}>
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full p-4 flex items-center justify-between hover:bg-dark-700/30 transition text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          {venueIcon(venue.venueType)}
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white truncate flex items-center gap-2">
              {venue.label}
              {sourceBadge(venue.source)}
            </div>
            <div className="text-[11px] text-gray-500 mt-0.5">
              {venue.venueType === 'cex'
                ? `${venue.holdings.length} asset${venue.holdings.length === 1 ? '' : 's'}`
                : `${venue.chainLabel || venue.chain || '—'} · ${venue.holdings.length} asset${venue.holdings.length === 1 ? '' : 's'}`}
              {venue.reason && <span className="ml-2 text-red-400">· {venue.reason.slice(0, 60)}</span>}
            </div>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-base font-bold text-white">{formatUsd(venue.totalUsd, { hidden: hideBalances })}</div>
          <div className="text-[10px] text-gray-500 uppercase tracking-wider">value</div>
        </div>
      </button>

      {expanded && !isUnavailable && venue.holdings.length > 0 && (
        <div className="border-t border-dark-700/80">
          <table className="w-full text-xs">
            <thead className="bg-dark-900/40">
              <tr className="text-[10px] uppercase tracking-wider text-gray-500">
                <th className="text-left py-2 px-4 font-medium">Asset</th>
                <th className="text-right py-2 px-4 font-medium">Balance</th>
                <th className="text-right py-2 px-4 font-medium">Available</th>
                <th className="text-right py-2 px-4 font-medium">USD value</th>
              </tr>
            </thead>
            <tbody>
              {venue.holdings.map(h => (
                <tr key={h.asset} className="border-t border-dark-700/40">
                  <td className="py-2 px-4 text-white font-mono">
                    {h.asset}
                    {h.native && <span className="ml-2 text-[9px] text-gray-500 uppercase">native</span>}
                  </td>
                  <td className="py-2 px-4 text-right text-gray-300 font-mono">{hideBalances ? '••••' : formatNum(h.balance)}</td>
                  <td className="py-2 px-4 text-right text-gray-500 font-mono">{hideBalances ? '••••' : formatNum(h.free)}</td>
                  <td className="py-2 px-4 text-right font-mono">
                    {h.priceUnavailable
                      ? <span className="text-amber-400 text-[10px]">price unavailable</span>
                      : <span className="text-white">{formatUsd(h.usdValue, { hidden: hideBalances })}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {expanded && isUnavailable && (
        <div className="border-t border-dark-700/80 p-4 text-xs text-amber-200/80 bg-amber-500/5 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            This venue could not be reached. {venue.reason ? `Reason: ${venue.reason}` : 'Will retry on next refresh.'}
          </div>
        </div>
      )}
    </div>
  );
}

function AssetGroupedTable({ assets, hideBalances }) {
  if (!assets || assets.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400 text-sm bg-dark-800/40 border border-dark-700 rounded-2xl">
        No assets across connected venues yet.
      </div>
    );
  }

  return (
    <div className="bg-dark-800/60 border border-dark-700 rounded-2xl overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-dark-900/40">
          <tr className="text-[10px] uppercase tracking-wider text-gray-500">
            <th className="text-left py-2.5 px-4 font-medium">Asset</th>
            <th className="text-right py-2.5 px-4 font-medium">Total balance</th>
            <th className="text-right py-2.5 px-4 font-medium">Total USD</th>
            <th className="text-left py-2.5 px-4 font-medium">Across venues</th>
          </tr>
        </thead>
        <tbody>
          {assets.map(a => (
            <tr key={a.asset} className="border-t border-dark-700/40">
              <td className="py-2.5 px-4 text-white font-mono">{a.asset}</td>
              <td className="py-2.5 px-4 text-right text-gray-300 font-mono">
                {hideBalances ? '••••' : formatNum(a.totalBalance)}
              </td>
              <td className="py-2.5 px-4 text-right text-white font-mono">
                {formatUsd(a.totalUsd, { hidden: hideBalances })}
              </td>
              <td className="py-2.5 px-4">
                <div className="flex flex-wrap gap-1">
                  {a.venues.map((v, i) => (
                    <span
                      key={i}
                      className={`text-[10px] px-1.5 py-0.5 rounded border ${v.venueType === 'cex' ? 'bg-blue-500/10 text-blue-300 border-blue-500/30' : 'bg-violet-500/10 text-violet-300 border-violet-500/30'}`}
                      title={`${v.label}: ${formatNum(v.balance)} (${formatUsd(v.usdValue)})`}
                    >
                      {v.label.split('·')[0].trim()}: {hideBalances ? '••' : formatNum(v.balance)}
                    </span>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
